import { useEffect, useState } from "react"
import { isNumber } from "lodash"
import { Backdrop, Box, Drawer } from "@mui/material"
import { DragIndicator } from "@mui/icons-material"
import useResizeHandler from "hooks/resize-handler"
import PropTypes from "prop-types"
import "./style.scss"

function getWidthPercent(width) {
  return width / window.innerWidth * 100
}

/**
 * Resizable Drawer
 * @param {Object} props
 * @param {number} props.initialWidth - Initial width of the drawer in pixels or percentage (needs to be a string with a %)
 * @param {React.ReactNode} props.children - Children of the drawer
 * @param {"left" | "right" | "top" | "bottom"} props.anchor - Side from which the drawer will appear
 * @param {boolean} props.open - If "true", the drawer is open
 * @param {function} props.onClose - Callback when the drawer is closed
 * @param {boolean} props.backdrop - If "true", a backdrop will be shown when the drawer is open
 * @param {number} props.minWidthPercent - Minimum width of the drawer in the viewport
 * @param {number} props.maxWidthPercent - Maximum width of the drawer in the viewport
 * @param {function} props.onResize - Callback when the drawer is resized, receives the width in percentage of the viewport (string with a %)
 * @param {boolean} props.closeOnBackdropClick - If "true", the drawer will be closed when the backdrop is clicked
 */
function ResizableDrawer({
  initialWidth,
  children,
  anchor = "right",
  open,
  onClose,
  backdrop = true,
  minWidthPercent = 25,
  maxWidthPercent = 50,
  onResize,
  closeOnBackdropClick,
}) {
  const [drawerWidth, setDrawerWidth] = useState(initialWidth || `${minWidthPercent}%`)
  const [isDragging, setIsDragging] = useState(false)

  // Ensure the drawer width is at least the minimum width
  useEffect(() => {
    if (typeof drawerWidth === "string" && drawerWidth.endsWith("%") && parseFloat(drawerWidth) < minWidthPercent) {
      setDrawerWidth(`${minWidthPercent}%`)
    } else if (isNumber(drawerWidth)) {
      if (getWidthPercent(drawerWidth) < minWidthPercent) {
        setDrawerWidth(`${minWidthPercent}%`)
      }
    }
  }, [drawerWidth, minWidthPercent])

  function handleResize(width) {
    setDrawerWidth(`${getWidthPercent(width)}%`)
  }

  function handleDragEnd(width) {
    if (onResize) {
      onResize(`${Math.round(getWidthPercent(width))}%`)
    }
  }

  const { handleMouseDown: handleResizerMouseDown } = useResizeHandler({
    isDragging,
    setIsDragging,
    setContentWidth: handleResize,
    onDragEnd: handleDragEnd,
    minWidthPercent,
    maxWidthPercent,
  })

  function handleBackdropClick() {
    if (closeOnBackdropClick) {
      onClose()
    }
  }

  return <div className="components_material_resizable-drawer">
    <Drawer
      variant="persistent"
      anchor={anchor}
      open={open}
      onClose={onClose}
      slotProps={{
          paper: {
            className: "drawer-container",
            sx: { width: drawerWidth },
            elevation: 10,
          },
        }}
    >
      <Box className="resize-handle" onMouseDown={handleResizerMouseDown}>
        <DragIndicator fontSize="small" />
      </Box>
      <Box className="contents" elevation={5}>
        {children}
      </Box>
    </Drawer>
    {backdrop && <Backdrop open={open} sx={{ zIndex: (theme) => theme.zIndex.drawer - 1 }} onClick={handleBackdropClick} />}
  </div>
}

export default ResizableDrawer

ResizableDrawer.propTypes = {
  initialWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  anchor: PropTypes.oneOf(["left", "right", "top", "bottom"]),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  backdrop: PropTypes.bool,
  minWidthPercent: PropTypes.number,
  maxWidthPercent: PropTypes.number,
  children: PropTypes.node,
  onResize: PropTypes.func,
  closeOnBackdropClick: PropTypes.bool,
}
